/**
 * Patient page communication tab: one timeline for SMS, e-mail and portal
 * messages, plus client-side normalisation of the old three-tab prefs.
 */

export const COMMUNICATION_TAB_ID = 'communication' as const;

export const OLD_COMMUNICATION_TAB_IDS = ['sms', 'email', 'messages'] as const;

export type OldCommunicationTabId = (typeof OLD_COMMUNICATION_TAB_IDS)[number];

export type CommunicationChannel = OldCommunicationTabId;

export type CommunicationFilter = 'all' | CommunicationChannel;

export type CommunicationDirection = 'outbound' | 'inbound';

const OLD_OR_NEW_TAB_IDS = new Set<string>([
  ...OLD_COMMUNICATION_TAB_IDS,
  COMMUNICATION_TAB_ID,
]);

const CHANNELS: readonly CommunicationChannel[] = OLD_COMMUNICATION_TAB_IDS;

function isCommunicationChannel(value: string): value is CommunicationChannel {
  return (CHANNELS as readonly string[]).includes(value);
}

function normalizeKey(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}

/**
 * Deep-link filter: `?tab=sms|email|messages|beskeder` or
 * `?tab=communication&channel=…`. Unknown / missing → Alle.
 */
export function communicationFilterFromParam(
  tab: string | null | undefined,
  channel?: string | null,
): CommunicationFilter {
  const tabKey = normalizeKey(tab);
  if (tabKey === 'beskeder') return 'messages';
  if (isCommunicationChannel(tabKey)) return tabKey;
  const channelKey = normalizeKey(channel);
  if (channelKey === 'beskeder') return 'messages';
  if (isCommunicationChannel(channelKey)) return channelKey;
  return 'all';
}

export function patientCommunicationPath(
  patientId: string,
  opts?: { channel?: CommunicationFilter; messageId?: string | null; threadId?: string | null },
): string {
  const params = new URLSearchParams();
  params.set('tab', COMMUNICATION_TAB_ID);
  if (opts?.channel && opts.channel !== 'all') params.set('channel', opts.channel);
  if (opts?.messageId) params.set('message', opts.messageId);
  if (opts?.threadId) params.set('thread', opts.threadId);
  return `/patients/${patientId}?${params.toString()}`;
}

/**
 * Saved tab order: the first of SMS / E-mail / Beskeder / Kommunikation is
 * replaced by `communication`; later old ids are dropped (de-duplicated).
 */
export function normalizePatientCommunicationTabOrder(
  order: readonly string[] | null | undefined,
): string[] | null {
  if (!order) return null;
  if (order.length === 0) return [];
  const result: string[] = [];
  let inserted = false;
  for (const key of order) {
    if (OLD_OR_NEW_TAB_IDS.has(key)) {
      if (!inserted) {
        result.push(COMMUNICATION_TAB_ID);
        inserted = true;
      }
      continue;
    }
    if (!result.includes(key)) result.push(key);
  }
  return result;
}

/**
 * Hidden tabs: Kommunikation is hidden only when every old channel tab was
 * hidden. A list that already stores `communication` (no old ids) is kept.
 */
export function normalizePatientCommunicationTabsHidden(
  hidden: readonly string[] | null | undefined,
): string[] {
  if (!hidden || hidden.length === 0) return [];
  const mentionsOld = OLD_COMMUNICATION_TAB_IDS.some((id) => hidden.includes(id));
  const hideCommunication = mentionsOld
    ? OLD_COMMUNICATION_TAB_IDS.every((id) => hidden.includes(id))
    : hidden.includes(COMMUNICATION_TAB_ID);
  const result: string[] = [];
  for (const key of hidden) {
    if (OLD_OR_NEW_TAB_IDS.has(key)) continue;
    if (!result.includes(key)) result.push(key);
  }
  if (hideCommunication) result.push(COMMUNICATION_TAB_ID);
  return result;
}

export function normalizePatientCommunicationPrefs<
  T extends { order: string[] | null; hidden: string[] },
>(prefs: T): T {
  return {
    ...prefs,
    order: normalizePatientCommunicationTabOrder(prefs.order),
    hidden: normalizePatientCommunicationTabsHidden(prefs.hidden),
  };
}

export interface CommunicationTimelineEntry {
  id: string;
  channel: CommunicationChannel;
  createdAt: string;
  direction: CommunicationDirection;
  searchText: string;
}

const CHANNEL_TIEBREAK: Record<CommunicationChannel, number> = {
  sms: 0,
  email: 1,
  messages: 2,
};

function timestampMs(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
}

/** Newest first; equal times keep a stable channel then id order. */
export function mergeCommunicationTimeline<T extends CommunicationTimelineEntry>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => {
    const byTime = timestampMs(b.createdAt) - timestampMs(a.createdAt);
    if (byTime !== 0) return byTime;
    const byChannel = CHANNEL_TIEBREAK[a.channel] - CHANNEL_TIEBREAK[b.channel];
    if (byChannel !== 0) return byChannel;
    return a.id.localeCompare(b.id);
  });
}

export function communicationChannelCounts(
  items: readonly Pick<CommunicationTimelineEntry, 'channel'>[],
): Record<CommunicationFilter, number> {
  const counts: Record<CommunicationFilter, number> = {
    all: items.length,
    sms: 0,
    email: 0,
    messages: 0,
  };
  for (const item of items) {
    counts[item.channel] += 1;
  }
  return counts;
}

export function filterCommunicationTimeline<T extends CommunicationTimelineEntry>(
  items: readonly T[],
  filter: CommunicationFilter,
  search: string,
): T[] {
  const query = search.trim().toLowerCase();
  return items.filter((item) => {
    if (filter !== 'all' && item.channel !== filter) return false;
    if (!query) return true;
    return item.searchText.toLowerCase().includes(query);
  });
}
